<script lang="ts">
    import {
        getModalStore,
        type ModalStore,
        Autocomplete,
        type AutocompleteOption,
        popup,
        getToastStore,
        type ToastStore,
    } from "@skeletonlabs/skeleton";
    import { slide } from "svelte/transition";

    import ProfilePicture from "$lib/components/ProfilePicture.svelte";

    import BlackX from "$lib/assets/BlackX.svg";
    import TrashGrey from "$lib/assets/TrashGrey.svg";

    const modalStore: ModalStore = getModalStore();
    const toastStore: ToastStore = getToastStore();

    // Parameters
    const type: "Writers" | "Reviewers" = $modalStore[0].meta.type;
    const question_list: any[] = $modalStore[0].meta.question_list;
    const users: any[] = $modalStore[0].meta.users;
    const application_id: string = $modalStore[0].meta.application_id;

    let question_users: any = {};

    let updating_question_collaborators: boolean = false;

    for (let question of question_list) {
        for (let user of question.user_question_permissions) {
            if ((type == "Writers" && user.role == "editor") || (type == "Reviewers" && user.role == "approver")) {
                if (!question_users[user.user_id]) {
                    question_users[user.user_id] = {
                        count: 0,
                        full_name: user.users.full_name,
                        email: user.users.email,
                    };
                }
                question_users[user.user_id].count += 1;
            }
        }
    }

    // Set up autocomplete input for adding team members to question, with a sort to enforce some regularity in ordering
    let question_collaborator_input: string = "";
    let user_add_options: AutocompleteOption<string>[];
    $: user_add_options = users
        .sort((a, b) => {
            return a.user_id > b.user_id ? 1 : -1;
        })
        .map((user) => {
            return {
                label: user.users.full_name ?? user.users.email,
                value: user.user_id,
                keywords: user.users.email + ", " + (user.users.full_name ?? "") + ", " + (user.users.department ?? ""),
            };
        })
        .filter(
            (ele) =>
                !Object.keys(question_users).find(
                    (user) => user == ele.value && question_users[user].count == question_list.length,
                ),
        );

    type collaborator_modal_response = {
        type: "add" | "delete";
        user_id: string;
        role: "editor" | "approver";
        user_info: { full_name: string; email: string };
    };

    async function handle_selection(event: CustomEvent<AutocompleteOption<string>>): Promise<void> {
        if (updating_question_collaborators) return;
        updating_question_collaborators = true;

        const resp = await fetch("/api/drafts/question_roles", {
            method: "POST",
            body: JSON.stringify({
                application_id: application_id,
                role_list: question_list.map((ele) => {
                    return {
                        question_id: ele.id,
                        user_id: event.detail.value,
                        role: type == "Writers" ? "editor" : "approver",
                    };
                }),
            }),
        });

        if (resp.ok) {
            const res = await resp.json();

            toastStore.trigger({
                message: "✓ " + res.message,
                background: "bg-zinc-800 text-green-300 text-center",
                hideDismiss: true,
                timeout: 4000,
            });

            const selected_user: any = users.find((ele) => ele.user_id == event.detail.value);

            question_users[event.detail.value] = {
                count: question_list.length,
                full_name: selected_user.users.full_name,
                email: selected_user.users.email,
            };

            $modalStore[0].response!({
                type: "add",
                user_id: event.detail.value,
                role: type == "Writers" ? "editor" : "approver",
                user_info: {
                    full_name: question_users[event.detail.value].full_name,
                    email: question_users[event.detail.value].email,
                },
            } as collaborator_modal_response);
        } else {
            await resp
                .json()
                .then((res) => {
                    toastStore.trigger({
                        message: "✗ " + res.message,
                        background: "bg-zinc-800 text-red-400 text-center",
                        hideDismiss: true,
                        timeout: 8000,
                    });
                })
                .catch(() => {
                    toastStore.trigger({
                        message: "✗ Something went wrong. Please try again or contact us if this error persists.",
                        background: "bg-zinc-800 text-red-400 text-center",
                        hideDismiss: true,
                        timeout: 8000,
                    });
                });
        }

        updating_question_collaborators = false;
        question_collaborator_input = "";
    }

    async function handle_deletion(user_id: string): Promise<void> {
        if (updating_question_collaborators) return;
        updating_question_collaborators = true;

        const resp = await fetch("/api/drafts/question_roles", {
            method: "DELETE",
            body: JSON.stringify({
                application_id: application_id,
                role_list: question_list.map((ele) => {
                    return { question_id: ele.id, user_id: user_id, role: type == "Writers" ? "editor" : "approver" };
                }),
            }),
        });

        if (resp.ok) {
            const res = await resp.json();

            toastStore.trigger({
                message: "✓ " + res.message,
                background: "bg-zinc-800 text-green-300 text-center",
                hideDismiss: true,
                timeout: 4000,
            });

            $modalStore[0].response!({
                type: "delete",
                user_id: user_id,
                role: type == "Writers" ? "editor" : "approver",
                user_info: { full_name: question_users[user_id].full_name, email: question_users[user_id].email },
            } as collaborator_modal_response);

            delete question_users[user_id];
            question_users = question_users;
        } else {
            await resp
                .json()
                .then((res) => {
                    toastStore.trigger({
                        message: "✗ " + res.message,
                        background: "bg-zinc-800 text-red-400 text-center",
                        hideDismiss: true,
                        timeout: 8000,
                    });
                })
                .catch(() => {
                    toastStore.trigger({
                        message: "✗ Something went wrong. Please try again or contact us if this error persists.",
                        background: "bg-zinc-800 text-red-400 text-center",
                        hideDismiss: true,
                        timeout: 8000,
                    });
                });
        }

        updating_question_collaborators = false;
    }
</script>

<div class="bg-white p-8 rounded-lg">
    <div class="flex items-center">
        <p class="font-bold">Edit {type}</p>
        <button
            class="ml-auto rounded-full hover:bg-stone-100 transition duration-300 p-1"
            on:click={() => modalStore.clear()}
        >
            <img src={BlackX} alt="Exit" />
        </button>
    </div>

    <p class="text-sm mt-4 mb-2">Add {type}</p>
    <input
        class="rounded-full text-sm w-80 border-zinc-400"
        type="text"
        placeholder="Add User"
        autocomplete="off"
        bind:value={question_collaborator_input}
        use:popup={{
            event: "focus-click",
            target: "popup-autocomplete",
            placement: "bottom",
        }}
    />
    <div data-popup={"popup-autocomplete"} class="bg-zinc-200 text-xs rounded-lg shadow-md py-3 w-80 z-[500]">
        <Autocomplete
            bind:input={question_collaborator_input}
            options={user_add_options}
            on:selection={handle_selection}
        />
    </div>

    <div class="grid grid-cols-[1fr_4fr_2fr_4fr] w-80 gap-2 mt-3">
        {#each Object.keys(question_users).toSorted() as collaborator}
            {@const collaborator_info = question_users[collaborator]}
            <div class="grid grid-cols-subgrid col-span-4 items-center" transition:slide={{ duration: 300 }}>
                <ProfilePicture
                    user_id={collaborator}
                    user_name={collaborator_info.full_name ?? collaborator_info.email}
                    size="8"
                />
                <p class="text-sm">{collaborator_info.full_name ?? collaborator_info.email}</p>
                <p class="text-sm text-zinc-300">{collaborator_info.count} of {question_list.length}</p>
                <button
                    class="px-4 py-2 rounded-full flex items-center hover:bg-stone-100 transition duration-300"
                    on:click={() => handle_deletion(collaborator)}
                >
                    <img src={TrashGrey} alt="Remove" />
                    <p class="ml-2 text-sm text-zinc-500">Remove</p>
                </button>
            </div>
        {/each}
    </div>
</div>
